import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { useAppDispatch } from "../../Redux/hooks";
import { setStateToggle } from "../../Redux/reducers/studySlice";
import { DrawerForm } from "../Common/styles/drawer";
import { debounce } from "lodash";
import { countries } from "../../const/countries";

let schema = yup.object().shape({
  name: yup.string().required("Site Name is Required"),
  siteAbbreviation: yup.string().required("Site Abbreviation is Required"),
  siteCountry: yup.string().required("Site Country is Required"),
  // siteCode: yup.string().optional(),
  // dateFormat: yup.string().required("Date format is Required"),
});

const AddSiteModal = ({ showModal, closeModal, data, studyId }: any) => {
  const [submitLoader, setSubmitLoader] = useState(false);

  const [searchLoader, setSearchLoader] = useState<boolean>(false);
  const [sitesList, setSitesList] = useState<any[]>([]);
  const dispatch = useAppDispatch();

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);

      let res: AxiosResponse;
      if (data?.id) {
        const body = {
          abbreviation: values?.siteAbbreviation || "",
          // dateFormat: values?.dateFormat || "",
        };
        res = await http.put(`/study/${studyId}/sites/${data.id}`, body);
      } else {
        const body = {
          name: values?.name || "",
          countryId: values?.siteCountry || "",
          // code: values?.siteCode || "",
          abbreviation: values?.siteAbbreviation || "",
          referenceId: values?.referenceId || "",
          // dateFormat: values?.dateFormat || "",
        };
        res = await http.post(`/study/${studyId}/sites`, body);
      }
      toastMessage("success", res?.data?.message);
      closeModal();
      setSubmitLoader(false);
      dispatch(setStateToggle());
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };
  const handleSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          setSearchLoader(true);
          setSitesList([]);
          let url = `/registry/sites?query=${value}`;

          const res: AxiosResponse = await http.get(url);
          const sites = res.data?.data;
          const newData = sites?.map((site: any) => ({
            id: site?.id,
            name: site?.name,
            siteAbbreviation: site?.abbreviation,
            siteCountry: site?.countryId,
            // siteCode: site?.code ?? "",
            country: site?.country?.label,
          }));

          setSitesList(newData);
          setSearchLoader(false);
        } catch (err) {
          errorToastMessage(err as Error);
          setSearchLoader(false);
        }
      }, 500),
    []
  );

  useEffect(() => {
    handleSearch("");
  }, [handleSearch]);

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={data?.id ? "Edit Site" : "Add Site"}
          onCloseClick={closeModal}
        />

        <Formik
          initialValues={{
            name: data?.siteName || "",
            siteCountry: data?.countryId || "",
            // siteCode: data?.code || "",
            siteAbbreviation: data?.abbreviation || "",
            referenceId: data?.referenceId || "",
            // dateFormat: data?.dateFormat || "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              {/* <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="site-name">
                    Site Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Site name"
                    id="site-name"
                    {...getFieldProps("name")}
                    error={touched?.name && errors?.name ? true : false}
                    helperText={
                      touched?.name && errors?.name
                        ? (errors?.name as string)
                        : " "
                    }
                  />
                </FormControl> */}
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle}>
                  Site Name <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Autocomplete
                  filterOptions={(x) => x}
                  onInputChange={(_1: any, value: any, reason: string) => {
                    if (reason === "input") {
                      handleSearch(value);
                      // setFieldValue("name", value);
                    }
                  }}
                  onChange={(_1: any, value: any) => {
                    // if (typeof value === "string") {
                    //   setFieldValue("name", value);
                    //   setFieldValue("siteAbbreviation", "");
                    //   setFieldValue("siteCountry", "");
                    //   setFieldValue("siteCode", "");
                    // } else
                    if (value && value.name) {
                      setFieldValue("name", value.name);
                      setFieldValue("siteAbbreviation", value.siteAbbreviation);
                      setFieldValue("siteCountry", value.siteCountry);
                      // setFieldValue("siteCode", value.siteCode);
                      setFieldValue("referenceId", value.id);
                    } else if (value === null) {
                      setFieldValue("name", "");
                      setFieldValue("siteAbbreviation", "");
                      setFieldValue("siteCountry", "");
                      // setFieldValue("siteCode", "");
                    }
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionTitle =
                      typeof option === "string" ? option : option.name;
                    const valueTitle =
                      typeof value === "string" ? value : value.name;
                    return optionTitle === valueTitle;
                  }}
                  value={values?.name || ""}
                  options={sitesList}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name
                  }
                  renderOption={(props: any, option: any) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        {option?.name}
                      </li>
                    );
                  }}
                  loading={searchLoader}
                  loadingText={<CircularProgress size={20} />}
                  noOptionsText="No Results"
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search Sites by typing..."
                    />
                  )}
                  readOnly={!!data?.id}
                />
                <FormHelperText
                  error={touched?.name && errors?.name ? true : false}
                >
                  {touched?.name && errors?.name
                    ? (errors?.name as string)
                    : " "}
                </FormHelperText>
              </FormControl>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="site-country">
                  Site Country <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  fullWidth
                  value={values.siteCountry || ""}
                  onChange={(e) => setFieldValue("siteCountry", e.target.value)}
                >
                  {countries.map((country: any) => (
                    <MenuItem value={country.value} key={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={
                    touched?.siteCountry && errors?.siteCountry ? true : false
                  }
                >
                  {touched?.siteCountry && errors?.siteCountry
                    ? (errors?.siteCountry as string)
                    : " "}
                </FormHelperText>
              </FormControl>

              {/* <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="site-code">
                    Site Code
                  </FormLabel>
                  <TextField
                    placeholder="Site code"
                    id="site-code"
                    {...getFieldProps("siteCode")}
                    error={touched?.siteCode && errors?.siteCode ? true : false}
                    helperText={
                      touched?.siteCode && errors?.siteCode
                        ? (errors?.siteCode as string)
                        : " "
                    }
                    inputProps={{ maxLength: 6 }}
                  />
                </FormControl> */}

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle} htmlFor="site-abbreviation">
                  Site Abbreviation <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <TextField
                  placeholder="Site abbreviation"
                  id="site-abbreviation"
                  {...getFieldProps("siteAbbreviation")}
                  error={
                    touched?.siteAbbreviation && errors?.siteAbbreviation
                      ? true
                      : false
                  }
                  helperText={
                    touched?.siteAbbreviation && errors?.siteAbbreviation
                      ? (errors?.siteAbbreviation as string)
                      : " "
                  }
                  inputProps={{ maxLength: 6 }}
                />
              </FormControl>

              {/* <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="date-format">
                    Date Format <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="YYYY-MM-DD"
                    id="date-format"
                    {...getFieldProps("dateFormat")}
                    error={
                      touched?.dateFormat && errors?.dateFormat ? true : false
                    }
                    helperText={
                      touched?.dateFormat && errors?.dateFormat
                        ? (errors?.dateFormat as string)
                        : " "
                    }
                  />
                </FormControl> */}
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        {data?.id ? "Save" : "Create"}
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddSiteModal;
